import React from 'react';
import styled, {css, withTheme} from 'styled-components';
import BigNumber from 'bignumber.js';
import parse, {domToReact} from 'html-react-parser';
import _ from 'lodash';
import replaceNewLinesForHtml from '../../utils/replaceNewLines';
import replaceNewLinesAndParse from '../../utils/replaceNewLinesAndParse';

function isHtml(value) {
  if (_.isNil(value)) {
    return false;
  }

  return _.trim(value).startsWith('<p>');
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px;
  background-color: white;
  border-radius: 10px;
  justify-items: stretch;
  align-items: stretch;
  box-shadow: rgba(0, 0, 0, 0.35) 2px 2px 4px 0px;
  margin-bottom: 20px;
  max-width: 500px;

  &.noImage {
    grid-template-columns: 1fr;
  }
`;

const NameContainer = styled.div`
  padding: 1rem;
  padding-bottom: .25rem;
  display: flex;
  border-top-left-radius: 10px;
  min-height: 65px;

  ${p => css`
    background-color: ${p.theme.teaser.backgroundColor};
  `}

  &.noImage {
    border-top-right-radius: 10px;
  }
`;

const Name = styled.div`
  ${p => css`
    font-family: ${p.theme.teaser.fontFamily};
    font-size: 22px;
    line-height: 30px;
    color: ${p.theme.teaser.color};
  `}
`;

const Motivation = styled.div`
  grid-column: 1 /span 2;
  padding: 1rem;
  padding-bottom: 0.5rem;
  display: flex;
  border-bottom-left-radius: 10px;
  ${p => css`

    font-size: ${p.theme.teaser.subtitleFontSize}
  `}
`;

const Image = styled.div`
  justify-self: end;
  height: 100px;
  width: 100%;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
  border-top-right-radius: 10px;
  ${p => css`
    background-color: ${p.theme.teaser.backgroundColor};
  `}
  ${p => p.url && css`
    background-image: url('${p.url}');
  `}
`;

const TargetContainer = styled.div`
  grid-column: 1 /span 2;
`;

const ProgressContainer = styled.div`
  margin: 1rem;
  height: 32px;
  border: 1px solid #DCDCD0;
  border-radius: 5px;
  margin-top: 0;
  ${p => css`
    background-color: ${p.theme.teaser.backgroundColor};
  `}
`;

const Progress = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  padding-right: 5px;
  height: 30px;
  white-space: nowrap;
  font-size: x-small;
  font-weight: bold;
  position: relative;
  ${p => css`
    background-color: ${p.theme.box.backgroundColorChecked};
    color: ${p.theme.box.fillColorChecked};
    width: ${p.value}%;
  `}

  ${p => p.value < 25 && css`
    &:after {
      transform: translateX(calc(100% + 10px));
      content: "€ ${p.revenue}";
    }
  `}

  ${p => p.value > 25 && css`
    &:after {
      content: "€ ${p.revenue}";
    }
  `}
`;

const Target = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  padding: 1rem;
  padding-top: 0;
  padding-bottom: .25rem;
  ${p => css`
    font-size: ${p.theme.teaser.subtitleFontSize}
  `}
`;

function DonationBoxSideBar({theme, children, dmBoxInfo, header, body}) {
  if (dmBoxInfo === undefined) {
    return null;
  }

  if (dmBoxInfo === false) {
    return children;
  }

  const moneyValue = BigNumber(dmBoxInfo?.totalRevenue_m || '0').toFormat();
  const targetMoneyValue = BigNumber(dmBoxInfo?.boxTargetRevenue_m || '0').toFormat();

  const options = {
    replace: (props) => {
      const firstChildData = _.get(props, ['children', 0, 'data']);
      const tagName = _.get(props, ['name']);

      if (tagName === 'p' && !(props.children.length === 1 && _.isString(firstChildData) && firstChildData.charCodeAt(0) === 160)) {
        return <p>{domToReact(props.children)}</p>;
      }

      if (tagName === 'li' && theme.teaser.display === 'inline') {
        return <li>{domToReact(props.children)}</li>;
      }

      if (tagName === 'h1') {
        return <h1>{domToReact(props.children)}</h1>;
      }
      if (tagName === 'h2') {
        return <h2>{domToReact(props.children)}</h2>;
      }
      if (tagName === 'h3') {
        return <h3>{domToReact(props.children)}</h3>;
      }
      if (tagName === 'h4') {
        return <h4>{domToReact(props.children)}</h4>;
      }
      if (tagName === 'h5') {
        return <h5>{domToReact(props.children)}</h5>;
      }
    }
  };

  const bodyHtml = isHtml(body) ? body : replaceNewLinesForHtml(body);
  const parsedBody = parse(bodyHtml, options);
  const motivation = dmBoxInfo?.boxMotivation_txt && replaceNewLinesAndParse(dmBoxInfo?.boxMotivation_txt);

  return <div><Container className={theme?.donationBox?.imageAsBackground && 'noImage'}>
    <NameContainer className={theme?.donationBox?.imageAsBackground && 'noImage'}>
      <Name>{dmBoxInfo?.boxHeader_txt || header}</Name>
    </NameContainer>

    {theme?.donationBox?.imageAsBackground ? <div /> : <Image url={dmBoxInfo?.image?.publicUrl} />}

    <Motivation>{motivation || parsedBody}</Motivation>

    <TargetContainer>
      <Target>
        <div>
          Doelbedrag
        </div>
        <div>
          € {targetMoneyValue}
        </div>
      </Target>

      <ProgressContainer>
        <Progress value={(dmBoxInfo?.percentageTarget || 0) <= 100 ? (dmBoxInfo?.percentageTarget || 0) : 100} revenue={moneyValue} />
      </ProgressContainer>
    </TargetContainer>
  </Container>
  </div>;
};

export default withTheme(DonationBoxSideBar);
